<template>
  <div>
    <v-dialog v-model="show" max-width="800px" :fullscreen="isMobile">
      <v-card class="late-checkout-dialog" tile>
        <v-toolbar elevation="0">
          <v-toolbar-title class="">Sen utcheckning</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon v-if="isMobile" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider></v-divider>

        <v-sheet
          class="d-flex overflow-y-auto overflow-x-hidden pa-4"
          :class="{ 'flex-column': isMobile }"
        >
          <div class="d-flex mr-2">
            <v-card outlined class="flex-shrink-1 flex-grow-0 mx-auto">
              <v-card-title class="primary white--text">
                Välj utcheckningsdatum
              </v-card-title>
              <v-date-picker
                v-model="datepickerInput"
                :min="datePickerMin"
                :max="datePickerMax"
                color="primary"
                :first-day-of-week="1"
                no-title
                :picker-date="displayedCalendarMonth"
                @update:picker-date="updateDisplayedMonth"
              ></v-date-picker>
            </v-card>
          </div>
          <div class="ml-2 d-flex overflow-x-hidden flex-column">
            <div class="pa-4">
              <v-alert
                type="warning"
                text
                class="alert-center-icon"
              >
                Bokningen skulle checkat ut {{ checkoutDateFormatted }}
              </v-alert>
              <v-select
                v-model="datepickerInput"
                :items="availableCheckoutDateItems"
                label="Välj bokningens verkliga utcheckningsdatum"
                outlined
                hide-details
                class="full-width"
              ></v-select>
            </div>
            <div class="full-width d-flex mt-auto">
              <v-btn
                @click="saveCheckoutDate"
                :disabled="!datepickerInput"
                :loading="isUpdatingCheckoutDate"
                class="mt-auto ml-auto"
                color="success"
                :block="isMobile"
                :x-large="isMobile"
              >
                Checka ut
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BookingService from '@/services/BookingService.js'

export default {
  name: 'LateCheckoutDialog',
  data: () => ({
    datepickerInput: null,
    displayedCalendarMonth: window.dayjs.today().format('YYYY-MM-DD'),
    isUpdatingCheckoutDate: false
  }),
  computed: {
    ...mapState(
      {
        booking: state => state.booking.lateCheckoutDialog.booking
      }
    ),
    show: {
      get () {
        return this.$store.state.booking.lateCheckoutDialog.show
      },
      set (value) {
        this.$store.commit('booking/SET_SHOW_LATE_CHECKOUT_DIALOG', value)
      }
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    checkoutDateFormatted: function () {
      if (!this.booking) {
        return ''
      }
      return this.booking.check_out.toHumanReadableDate({ longFormat: true, capitalized: false })
    },
    datePickerMax: function () {
      return window.dayjs.today().format('YYYY-MM-DD')
      // REVIEW: eventuellt är det dumt att kunna välja datum som är bokade av icke-incheckade bokningar - men det är ju möjligt att denna bokningen stått på platsen när någon annan bokning egentligen skulle haft den men inte checkat in
      // TODO: dialogen skulle egentligen kunna använda availableCheckoutDates metoden så som sen incheckningsdialogen gör, för att vara helt säker på att undvika tenancy konflikter
    },
    datePickerMin: function () {
      if (!this.booking) {
        return this.datePickerMax
      }
      return this.booking.check_out.format('YYYY-MM-DD')
    },
    availableCheckoutDateItems: function () {
      if (!this.booking) {
        return []
      }
      const items = []
      const checkOutDate = this.booking.check_out
      const today = window.dayjs.today()
      let iterableDate = checkOutDate
      const parenthesisText = matchingDate => {
        if (matchingDate.isSame(this.booking.check_in, 'day')) {
          return ' (bokningens incheckningsdatum)'
        } else if (matchingDate.isSame(this.booking.check_out, 'day')) {
          return ' (bokningens utcheckningsdatum)'
        } else {
          return ''
        }
      }
      while (iterableDate.isBefore(today) || iterableDate.isSame(today, 'day')) {
        items.push({
          text: iterableDate.toHumanReadableDate({ longFormat: true, capitalized: true }) + parenthesisText(iterableDate),
          value: iterableDate.format('YYYY-MM-DD')
        })
        iterableDate = iterableDate.add(1, 'day')
      }
      return items
    }
  },
  methods: {
    close: function () {
      this.show = false
    },
    updateDisplayedMonth: function (dt) {
      // månaden som visas i kalendern, i formatet 'YYYY-MM-DD'
      this.displayedCalendarMonth = dt
    },
    saveCheckoutDate: function () {
      if (this.booking.check_out.format('YYYY-MM-DD') === this.datepickerInput) {
        // valt samma utcheckningsdatum som redan är på bokningen, behöver inte skicka till servern
        this.$router.push({ name: 'PosRegister', params: { chargable: 'booking', id: this.booking.id, checkout: 'checkout' } })
        this.close()
      } else {
        // valt nytt utcheckningsdatum på bokningen, skicka till server för att uppdatera datum och platsavgifter sen routa till kassan
        const bookingId = this.booking.id
        const checkoutDate = window.dayjs.parseDate(this.datepickerInput)
        this.isUpdatingCheckoutDate = true
        BookingService.updateCheckoutDate({ bookingId, checkoutDate })
          .then(({ data }) => {
            if (data.status === 'success') {
              this.close()
              this.$router.push({ name: 'PosRegister', params: { chargable: 'booking', id: this.booking.id, checkout: 'checkout' } })
            }
          })
          .finally(() => {
            // Behöver inte hämta om booking history, eftersom booking dialog stängs
            this.isUpdatingCheckoutDate = false
          })
      }
    }
  },
  watch: {
    show: function (newVal) {
      if (newVal) {
        // dialogen öppnas
        this.displayedCalendarMonth = this.booking ? this.booking.check_out.startOf('month').format('YYYY-MM-DD') : window.dayjs.today().startOf('month').format('YYYY-MM-DD')
      } else {
        // dialogen stängs
        this.datepickerInput = null
        this.isUpdatingCheckoutDate = false
      }
    }
  }
}
</script>
